import { useState } from 'react';
import * as React from 'react';
import { NavLink, useHistory } from 'react-router-dom';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import ExitToAppIcon from '@mui/icons-material/ExitToApp';
import HelpIcon from '@mui/icons-material/Help';
import MenuIcon from '@mui/icons-material/Menu';
import MenuOpenIcon from '@mui/icons-material/MenuOpen';
import {
  Avatar,
  Button,
  Divider,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Skeleton,
  Toolbar,
  Typography,
  useTheme,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import paths from '../router/paths';
import Auth from '../services/auth';
import { useStateToProps } from '../store/actions/actions';
import Notification from './notification';

const StyledToolbar = styled(Toolbar)(({ theme }) => ({
  backgroundColor: theme.palette.background.paper,
  color: theme.palette.text.primary,
  // boxShadow: theme.shadows[1],
}));

const MenuToggleButton = styled(IconButton)(({ theme }) => ({
  color: theme.palette.background.dark,
  '&:hover': {
    backgroundColor: theme.palette.action.hover,
  },
}));

const AppTitle = styled(Typography)(({ theme }) => ({
  flexGrow: 1,
  fontWeight: 600,
  display: 'flex',
}));

const StyledMenu = styled(Menu)(({ theme }) => ({
  '& .MuiPaper-root': {
    marginTop: '10px',
    border: '1px solid #d3d4d5',
    width: '170px',
    '& > ul': {
      padding: 0,
    },
    [theme.breakpoints.down('sm')]: {
      width: '150px',
      marginTop: '5px',
    },
  },
}));

const StyledMenuItem = styled(MenuItem)(({ theme }) => ({
  display: 'flex',
  margin: '5px 0',
  '& .MuiListItemIcon-root': {
    minWidth: '30px',
  },
  '& .MuiListItemText-root': {
    margin: '0',
  },
  '& > a': {
    display: 'flex',
  },
  [theme.breakpoints.down('sm')]: {
    margin: '0',
    minHeight: '40px',
  },
}));

const RightMenu = styled('div')({
  display: 'flex',
  alignItems: 'center',
  alignContent: 'center',
});

const HelpIconStyled = styled('a')({
  width: '20px',
  height: '20px',
  display: 'flex',
});
const AvatarContainer = styled('div')({
  marginTop: '4px',
  backgroundColor: '#f3f6f8',
  backgroundSize: 'cover',
  width: '40px',
  height: '40px',
  borderRadius: '50%',
});

function TopBar({ onToggleClick, isMobile, drawerOpen, setDrawerOpen }) {
  const [anchorEl, setAnchorEl] = useState(null);
  const isMenuOpen = Boolean(anchorEl);
  const menuId = 'account-menu';
  const history = useHistory();
  const isAuthenticated = Auth().isAuthenticated();
  const theme = useTheme();

  const { userData, appLoading } = useStateToProps((state) => ({
    userData: state.app.user,
    appLoading: state.global.appLoading,
  }));

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    Auth().logout();
    handleMenuClose();
  };
  const handleProfileMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const renderMenu = () => (
    <StyledMenu
      id={menuId}
      anchorEl={anchorEl}
      keepMounted
      open={isMenuOpen}
      onClose={handleMenuClose}
    >
      <StyledMenuItem>
        <NavLink to={paths.profile}>
          <ListItemIcon>
            <AccountCircleIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary="Profile" />
        </NavLink>
      </StyledMenuItem>
      <Divider />
      <StyledMenuItem onClick={handleLogout} sx={{ color: 'error.main' }}>
        <ListItemIcon>
          <ExitToAppIcon fontSize="small" sx={{ color: 'error.main' }} />
        </ListItemIcon>
        <ListItemText primary="Logout" />
      </StyledMenuItem>
    </StyledMenu>
  );

  const renderAuthentication = () => {
    if (appLoading) {
      return <Skeleton width="40px" height="40px" variant="circular" animation="wave" />;
    }
    return (
      <>
        {renderMenu()}
        <RightMenu>
          <HelpIconStyled
            target="_blank"
            href="https://wiki.quickclaim.io/knowledge/connect-to-ndis-api"
            rel="noreferrer"
          >
            <HelpIcon color="action" />
          </HelpIconStyled>
          <Notification />
          {!isAuthenticated ? (
            <Button variant="outlined" color="primary" onClick={() => history.push(paths.login)}>
              Sign in
            </Button>
          ) : (
            <AvatarContainer>
              <Avatar alt="user" src={userData?.picture} onClick={handleProfileMenuOpen} />
            </AvatarContainer>
          )}
        </RightMenu>
      </>
    );
  };

  return (
    <StyledToolbar>
      {isMobile ? (
        <MenuToggleButton
          aria-label="open drawer"
          edge="start"
          onClick={onToggleClick}
          sx={{ mr: 2, display: { sm: 'none' } }}
        >
          <MenuIcon />
        </MenuToggleButton>
      ) : (
        <MenuToggleButton
          aria-label={drawerOpen ? 'collapse menu' : 'expand menu'}
          edge="start"
          onClick={() => setDrawerOpen(!drawerOpen)}
          sx={{
            mr: 2,
            transition: theme.transitions.create('transform', {
              easing: theme.transitions.easing.sharp,
              duration: theme.transitions.duration.enteringScreen,
            }),
          }}
        >
          {drawerOpen ? <MenuIcon /> : <MenuOpenIcon />}
        </MenuToggleButton>
      )}

      <AppTitle variant="h6" noWrap component="div">
        {userData.orgName && (
          <Typography
            color="textSecondary"
            variant="body2"
            style={{
              display: 'inline-flex',
              height: '20px',
              lineHeight: '20px',
            }}
          >
            <span>{userData.orgName}</span>
          </Typography>
        )}
      </AppTitle>
      {renderAuthentication()}
    </StyledToolbar>
  );
}

export default TopBar;
